import { Outlet, useParams } from "react-router";
import {
  NavigationGroup,
  NavigationItem,
  ProcessPage,
  ProcessPageMain,
  ProcessPageMainContent,
  ProcessPageSidebar,
  ProcessPageSidebarFooter,
  ProcessPageSidebarNavigation,
  ProcessPageSkeleton,
} from "@/shared/bsm/core/process/components/ui/process-page";
import t from "@/lang/lang";
import { useProcessesProcessShowQuery } from "@/services/backend/processes/api/process-api";
import { RTKQueryErrorAlert } from "@/shared/components/errors/rtk-query-error-alert";
import { CloseProcessSidebarMenu } from "@/routes/processes/[processId]/_components/close-process-sidebar-menu";
import {
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
} from "@/shared/components/ui/sidebar";
import { DynamicsSalesLinksSidebarGroup } from "@/routes/processes/[processId]/_components/dynamics-sales-links-sidebar-group";
import { GSMProcessKind } from "@/services/backend/processes/types/process-kind";
import {
  Commitments,
  Confirmations,
  Contacts,
  Offers,
  Orders,
  Overview,
  Reports,
  WorkOrders,
} from "@/shared/bsm/core/process/components/ui/process-page-navigation-item";
import { ProcessKind } from "@bitory-gmbh/bsm";
import { useFeatureAuth } from "@/services/backend/auth/hooks/use-feature-auth";
import { Alert403 } from "@/shared/components/alerts/alert-403";
import { MainPageContent } from "@/routes/_components/main-page";
import { useBooleanFlagValue } from "@openfeature/react-sdk";

export default function HTZRoute() {
  const { processId } = useParams();
  const {
    data: process,
    isLoading,
    error,
  } = useProcessesProcessShowQuery({
    id: processId ?? "",
  });

  const sidebarNavigation = useSidebarNavigation(process?.processKind);

  const isReleased = useBooleanFlagValue("gsm.htz", true);
  const canAccess = useFeatureAuth("gsm.htz");
  if (!canAccess || !isReleased) {
    return (
      <MainPageContent>
        <Alert403 />
      </MainPageContent>
    );
  }

  return (
    <ProcessPage>
      {isLoading && <ProcessPageSkeleton />}
      {error && (
        <ProcessPageMain>
          <ProcessPageMainContent>
            <RTKQueryErrorAlert error={error} />
          </ProcessPageMainContent>
        </ProcessPageMain>
      )}
      {process && (
        <>
          <ProcessPageSidebar className="pt-16">
            <ProcessPageSidebarNavigation groups={sidebarNavigation} />
            <ProcessPageSidebarFooter>
              <SidebarGroup>
                <SidebarGroupLabel>{t("Vorgang")}</SidebarGroupLabel>
                <SidebarGroupContent>
                  <CloseProcessSidebarMenu processId={process.id} />
                </SidebarGroupContent>
              </SidebarGroup>
              <DynamicsSalesLinksSidebarGroup processId={process.id} />
            </ProcessPageSidebarFooter>
          </ProcessPageSidebar>
          <Outlet />
        </>
      )}
    </ProcessPage>
  );
}

function useSidebarNavigation(processKind?: ProcessKind): NavigationGroup[] {
  const offersReleased = useBooleanFlagValue("gsm.htz.offers", true);
  const ordersReleased = useBooleanFlagValue("gsm.htz.orders", true);
  const confirmationsReleased = useBooleanFlagValue(
    "gsm.htz.confirmations",
    true,
  );
  const workordersReleased = useBooleanFlagValue("gsm.htz.workorders", true);
  const reportsReleased = useBooleanFlagValue("gsm.htz.reports", true);
  const commitmentsReleased = useBooleanFlagValue("gsm.htz.commitments", true);

  const itemFilter = (item: NavigationItem) => {
    switch (item.to) {
      case "offers":
        return offersReleased;
      case "orders":
        return ordersReleased;
      case "confirmations":
        return confirmationsReleased;
      case "commitments":
        return commitmentsReleased;
      case "work-orders":
        return workordersReleased;
      case "reports":
        return reportsReleased;
      default:
        return false;
    }
  };

  if (!processKind) {
    return [];
  }

  switch (processKind) {
    case GSMProcessKind.Full:
      return [
        {
          label: t("Allgemein"),
          items: [Overview, Contacts],
        },
        {
          label: t("Meilensteine"),
          items: [
            Offers,
            Orders,
            Confirmations,
            Commitments,
            WorkOrders,
            Reports,
          ].filter(itemFilter),
        },
      ];
    case GSMProcessKind.Delivery:
      return [
        {
          label: t("Allgemein"),
          items: [Overview, Contacts],
        },
        {
          label: t("Meilensteine"),
          items: [WorkOrders, Reports].filter(itemFilter),
        },
      ];
    default:
      return [];
  }
}
